import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SquareCheckBig, CircleAlert } from "lucide-react";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import PhoneInput from "../../../components/ui/PhoneInput";
import CountryDropdown from "../../../components/ui/CountryDropdown";
import { companySizes, countryOptions, industries, legalStatusOptions } from "./../../shared";
import { CompanyFormData } from "../../user/models";
import { useSelector } from "react-redux";
import {
    createUserCompany,
    getUserCompanies,
    updateUserCompanies,
    useAppDispatch,
    validateVATNumber,
} from "../../../redux";
import { selectLoaderValue, showLoader, hideLoader } from "../../../redux/Slices/loadingSlice";
import Spinner from "../../shared/components/Spinner";
import { RoleEnum } from "../../shared/model";
import { GlobalService } from "../../shared";

export default function CompanyProfileSetting() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const isLoading = useSelector(selectLoaderValue);
    const { company } = useSelector((state: any) => state.client);
    const { user } = useSelector((state: any) => state.auth);
    const { countries } = useSelector((state: any) => state.admin || { countries: [] });
    const [formData, setFormData] = useState<CompanyFormData>({
        name: "",
        tradingName: "",
        industry: "",
        size: "",
        description: "",
        foundedYear: "",
        phone: "",
        registrationNumber: "",
        vatNumber: "",
        taxId: "",
        legalStatus: "",
        address: {
            street: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
        },
        email: "",
        website: "",
        minHourly: "",
        maxHourly: "",
    });
    const [selectedCountry, setSelectedCountry] = useState<{ id: string; name: string; code: string } | null>(null);
    // Check if user role is 4 (needs hourly rates)
    const isRole4 =
        Number(user?.role?.id) === RoleEnum.professional ||
        Number(user?.role?.id) === RoleEnum.internationl_professional;
    // Map company data to formData when company data is available
    useEffect(() => {
        if (company) {
            setFormData({
                name: company.name || "",
                tradingName: company.trading || "",
                industry: company.industry?.id || "", // companySize, legalStatus, etc. are objects with id
                size: company.companySize?.id || "", // Assuming "id" refers to the selected option
                description: company.description || "",
                foundedYear: company.year || "",
                phone: company.phone || "",
                registrationNumber: company.registrationNumber || "",
                vatNumber: company.vatNumber || "",
                taxId: company.taxId || "",
                legalStatus: company.legalStatus?.id || "",
                address: {
                    street: company.address || "",
                    city: company.city || "",
                    state: company.state || "",
                    postalCode: company.postalCode || "",
                    country: company.country || "",
                },
                email: company.email || "",
                website: company.website || "",
                minHourly: company.minHourly || "",
                maxHourly: company.maxHourly || "",
            });

            // Set selected country if company has country data
            if (company.country) {
                // We'll set the country when the countries are loaded from Redux store
                // This will be handled in a separate useEffect that watches for countries data
                console.log("Company has country data:", company.country);
            }
        }
    }, [company]);

    // Handle country selection when both company data and countries are available
    useEffect(() => {
        if (company?.country && !selectedCountry && countries && countries.length > 0) {
            // Find the country by ID in the countries list
            const foundCountry = countries.find((country: any) => country.id === company.country);
            if (foundCountry) {
                setSelectedCountry({
                    id: foundCountry.id,
                    name: foundCountry.name,
                    code: foundCountry.code
                });
                console.log("Country selected from company data:", foundCountry);
            }
        }
    }, [company?.country, selectedCountry, countries]);

    // Reset selected country when company changes
    useEffect(() => {
        if (company?.country && countries && countries.length > 0) {
            const foundCountry = countries.find((country: any) => country.id === company.country);
            if (foundCountry && (!selectedCountry || selectedCountry.id !== foundCountry.id)) {
                setSelectedCountry({
                    id: foundCountry.id,
                    name: foundCountry.name,
                    code: foundCountry.code
                });
                console.log("Country updated from company data:", foundCountry);
            }
        }
    }, [company?.country, countries]);

    const handleInputChange = (field: string, value: string | object) => {
        const updatedFormData = {
            ...formData,
            [field]: value,
        };

        setFormData(updatedFormData);

        // setErrors(validate(updatedFormData)); // Re-run validation on change
    };

    const handleCountrySelect = (country: { id: string; name: string; code: string } | null) => {
        setSelectedCountry(country);
        const updatedAddress = {
            ...formData.address,
            country: country?.id || "",
        };
        handleInputChange("address", updatedAddress);
        console.log("Country selected:", country, "Form data country:", updatedAddress.country);
    };

    // Function to extract country code from VAT number
    const extractCountryCodeFromVAT = (vatNumber: string): string => {
        if (!vatNumber || vatNumber.length < 2) return "";

        // VAT numbers typically start with 2 letters (country code)
        const countryCode = vatNumber.substring(0, 2).toUpperCase();

        // Validate that it's actually letters
        if (/^[A-Z]{2}$/.test(countryCode)) {
            return countryCode;
        }

        return "";
    };

    // Function to format VAT number input - removes country code and keeps only numbers
    const formatVATNumber = (value: string): string => {
        // Remove all non-alphanumeric characters
        const cleaned = value.replace(/[^A-Za-z0-9]/g, "");

        // Convert to uppercase
        const upperCase = cleaned.toUpperCase();

        // If it starts with 2 letters (country code), remove them
        if (/^[A-Z]{2}/.test(upperCase)) {
            return upperCase.substring(2);
        }

        return upperCase;
    };

    const validate = (formData: any) => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) newErrors.name = t("companyProfileSettings.validation.companyNameRequired");
        if (!formData.description) newErrors.description = t("companyProfileSettings.validation.descriptionRequired");
        if (!formData.industry) newErrors.industry = t("companyProfileSettings.validation.industryRequired");
        if (!formData.size) newErrors.size = t("companyProfileSettings.validation.companySizeRequired");

        const foundedYearNumber = parseInt(formData.foundedYear, 10);
        const foundedYearStr = String(formData.foundedYear).trim(); // Convert to string first

        if (
            !foundedYearStr ||
            isNaN(Number(foundedYearStr)) ||
            Number(foundedYearStr) < 1900 ||
            Number(foundedYearStr) > new Date().getFullYear()
        ) {
            newErrors.foundedYear = t("companyProfileSettings.validation.foundedYearInvalid");
        }
        if (isRole4) {
            if (!formData.legalStatus) newErrors.legalStatus = t("companyProfileSettings.validation.legalStatusRequired");
            if (!formData.phone.trim()) newErrors.phone = t("companyProfileSettings.validation.phoneRequired");
            if (!formData.email.trim()) newErrors.email = t("companyProfileSettings.validation.emailRequired");
            // if (!formData.website.trim()) newErrors.website = "Website URL is required.";

            if (!formData.address.street.trim()) newErrors.street = t("companyProfileSettings.validation.streetRequired");
            if (!formData.address.city.trim()) newErrors.city = t("companyProfileSettings.validation.cityRequired");
            if (!formData.address.state.trim()) newErrors.state = t("companyProfileSettings.validation.stateRequired");
            if (!formData.address.postalCode.trim()) newErrors.postalCode = t("companyProfileSettings.validation.postalCodeRequired");
            if (!formData.address.country.trim()) newErrors.country = t("companyProfileSettings.validation.countryRequired");

            if (!formData.minHourly) {
                newErrors.minHourly = t("companyProfileSettings.validation.minHourlyRequired");
            } else if (isNaN(Number(formData.minHourly)) || Number(formData.minHourly) < 0) {
                newErrors.minHourly = t("companyProfileSettings.validation.minHourlyInvalid");
            }

            if (!formData.maxHourly) {
                newErrors.maxHourly = t("companyProfileSettings.validation.maxHourlyRequired");
            } else if (isNaN(Number(formData.maxHourly)) || Number(formData.maxHourly) < 0) {
                newErrors.maxHourly = t("companyProfileSettings.validation.maxHourlyInvalid");
            }

            // Validate that max is greater than min if both exist
            if (formData.minHourly && formData.maxHourly && Number(formData.maxHourly) < Number(formData.minHourly)) {
                newErrors.maxHourly = t("companyProfileSettings.validation.maxHourlyGreater");
            }
        }
        return newErrors;
    };

    useEffect(() => {
        getCompanyData();
    }, []);

    const getCompanyData = async () => {
        if (company == null) {
            await dispatch(getUserCompanies({ id: user.id }));
        }
    };
    const handleSave = async () => {
        console.log("Form submission started for role:", user?.role?.id, "isRole4:", isRole4);
        const validationErrors = validate(formData);
        setErrors(validationErrors);

        console.log("Validation errors:", validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            console.log("Form validation failed, stopping submission");
            return;
        }

        console.log("Form validation passed, proceeding with save");

        // Show loader while saving
        dispatch(showLoader());

        try {
            const isVatValid = false;

            // Only perform VAT validation for non-role 4 users
            // if (!isRole4) {
            //     // Check if VAT number is new or changed (only if company exists)
            //     const shouldValidateVAT = true;// !company || (company && company.vatNumber !== formData.vatNumber);

            //     if (shouldValidateVAT && formData.vatNumber) {
            //         // Extract country code from the VAT number
            //         const extractedCountryCode = extractCountryCodeFromVAT(formData.vatNumber);

            //         if (!extractedCountryCode) {
            //             GlobalService.showToast("error", "VAT Validation", "Invalid VAT number format. VAT number should start with a 2-letter country code (e.g., CZ03571786)");
            //             return;
            //         }

            //         // Extract VAT number without country code
            //         const vatNumberWithoutCountryCode = formData.vatNumber.substring(2);

            //         dispatch(showLoader());
            //         try {
            //             // Validate VAT number if it's new or changed
            //             const vatValidationResponse = await dispatch(validateVATNumber({
            //                 countryCode: extractedCountryCode,
            //                 vatNumber: vatNumberWithoutCountryCode
            //             })).unwrap();
            //             isVatValid = vatValidationResponse.valid;

            //             if (vatValidationResponse.valid) {
            //                 GlobalService.showToast("success", "VAT Validation", "VAT number is valid");
            //             } else {
            //                 GlobalService.showToast("error", "VAT Validation", "VAT number is invalid");
            //             }
            //         } catch (error) {
            //             GlobalService.showToast("error", "VAT Validation", "Failed to validate VAT number");
            //             isVatValid = false;
            //         }
            //     } else if (company) {
            //         // Use existing VAT validation status if not changed
            //         isVatValid = company.isVatValid || false;
            //     }
            // } else {
            //     // For role 4 users, skip VAT validation entirely
            //     isVatValid = true; // Set to true to avoid any validation issues
            // }

            const payload = {
                isVatValid,
                legalStatus: formData.legalStatus.length === 0 ? { id: 1 } : { id: Number(formData.legalStatus) },
                industry: { id: Number(formData.industry) },
                userId: { id: user?.id },
                companySize: { id: Number(formData.size) },
                postalCode: formData.address.postalCode,
                state: formData.address.state,
                city: formData.address.city,
                address: formData.address.street,
                phone: formData.phone,
                email: formData.email,
                country: formData.address.country,
                website: formData.website,
                taxId: formData.taxId,
                vatNumber: formData.vatNumber,
                registrationNumber: formData.registrationNumber,
                year: parseInt(formData.foundedYear, 10),
                description: formData.description,
                trading: formData.tradingName,
                name: formData.name,
                ...(isRole4 && {
                    minHourly: Number(formData.minHourly),
                    maxHourly: Number(formData.maxHourly),
                }),
            };

            console.log("Form data being saved:", {
                country: formData.address.country,
                selectedCountry: selectedCountry,
                fullAddress: formData.address
            });

            if (!company) {
                console.log("Creating new company with payload:", payload);
                await dispatch(createUserCompany({ data: payload }));
                console.log("Company created successfully");
            } else {
                console.log("Updating existing company with payload:", payload);
                await dispatch(updateUserCompanies({ id: company?.id, data: payload }));
                console.log("Company updated successfully");
            }
        } catch (error) {
            console.error("Error saving company:", error);
            // Handle error (maybe show a notification to the user)
        } finally {
            // Hide loader after save operation completes
            dispatch(hideLoader());
        }
    };

    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}
            <div className={`space-y-6 ${isLoading ? "opacity-25" : "opacity-100"}`}>
                {/* Basic Company Information */}
                <Card color={undefined} className="p-6">
                    <h2 className="text-xl font-semibold mb-6">{t("companyProfileSettings.companyInformation")}</h2>
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.vatNumber")}</label>
                                <input
                                    type="text"
                                    value={formData.vatNumber}
                                    onChange={e => handleInputChange("vatNumber", e.target.value.toUpperCase())}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder={t("companyProfileSettings.vatNumberPlaceholder")}
                                />
                                {errors.vatNumber && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.vatNumber}
                                    </div>
                                )}
                                {/* {(company?.isVatValid ? (
                                    <div className="flex items-center text-green-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        Vat Number Verified
                                    </div>
                                ) : (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        {!isRole4 &&
                                            <>
                                                <CircleAlert className="w-4 h-4 mr-1" />
                                                Enter your VAT number and save. The system will update automatically.
                                            </>
                                        }
                                    </div>
                                ))} */}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.legalStatus")}</label>
                                <select
                                    value={formData.legalStatus}
                                    onChange={e => handleInputChange("legalStatus", e.target.value)}
                                    className="w-full p-2 border rounded-lg">
                                    <option value="">{t("companyProfileSettings.selectLegalStatus")}</option>
                                    {legalStatusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.legalStatus && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.legalStatus}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.companyName")}*</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={e => handleInputChange("name", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder={t("companyProfileSettings.companyNamePlaceholder")}
                                />
                                {errors.name && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.name}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.tradingName")}</label>
                                <input
                                    type="text"
                                    value={formData.tradingName}
                                    onChange={e => handleInputChange("tradingName", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder={t("companyProfileSettings.tradingNamePlaceholder")}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.description")}*</label>
                            <textarea
                                value={formData.description}
                                onChange={e => handleInputChange("description", e.target.value)}
                                rows={4}
                                className="w-full p-2 border rounded-lg"
                                placeholder={t("companyProfileSettings.descriptionPlaceholder")}
                            />
                            {errors.description && (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                    <CircleAlert className="w-4 h-4 mr-1" />
                                    {errors.description}
                                </div>
                            )}
                        </div>
                        {isRole4 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.minHourlyRate")}*</label>
                                    <input
                                        type="number"
                                        value={formData.minHourly}
                                        onChange={e => handleInputChange("minHourly", e.target.value)}
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.minHourlyPlaceholder")}
                                        min="0"
                                        step="0.01"
                                    />
                                    {errors.minHourly && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.minHourly}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.maxHourlyRate")}*</label>
                                    <input
                                        type="number"
                                        value={formData.maxHourly}
                                        onChange={e => handleInputChange("maxHourly", e.target.value)}
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.maxHourlyPlaceholder")}
                                        min="0"
                                        step="0.01"
                                    />
                                    {errors.maxHourly && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.maxHourly}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.industry")}*</label>
                                <select
                                    value={formData.industry}
                                    onChange={e => handleInputChange("industry", e.target.value)}
                                    className="w-full p-2 border rounded-lg">
                                    <option value="">{t("companyProfileSettings.selectIndustry")}</option>
                                    {industries.map(industry => (
                                        <option key={industry.value} value={industry.value}>
                                            {industry.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.industry && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.industry}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.companySize")}*</label>
                                <select
                                    value={formData.size}
                                    onChange={e => handleInputChange("size", e.target.value)}
                                    className="w-full p-2 border rounded-lg">
                                    <option value="">{t("companyProfileSettings.selectSize")}</option>
                                    {companySizes.map(size => (
                                        <option key={size.value} value={size.value}>
                                            {size.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.size && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.size}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.foundedYear")}*</label>
                                <input
                                    type="number"
                                    value={formData.foundedYear}
                                    onChange={e => handleInputChange("foundedYear", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder="YYYY"
                                    min="1900"
                                    max={new Date().getFullYear()}
                                />
                                {errors.foundedYear && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.foundedYear}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>
                {/* Registration & Legal Information */}
                {/* <Card color={undefined} className="p-6">
                    <h2 className="text-xl font-semibold mb-6">Registration & Legal Information</h2>
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Company Registration Number</label>
                                <input
                                    type="text"
                                    value={formData.registrationNumber}
                                    onChange={e => handleInputChange("registrationNumber", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder="Enter registration number"
                                />
                                {errors.registrationNumber && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.registrationNumber}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">VAT Number</label>
                                <input
                                    type="text"
                                    value={formData.vatNumber}
                                    onChange={e => handleInputChange("vatNumber", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder="Enter VAT number"
                                />
                                {errors.vatNumber && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.vatNumber}
                                    </div>
                                )}
                                {company != null && (company?.isVatValid ? (
                                    <div className="flex items-center text-green-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        Verified Vat Number
                                    </div>
                                ) : (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        Un Verified Vat Number
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Tax ID</label>
                                <input
                                    type="text"
                                    value={formData.taxId}
                                    onChange={e => handleInputChange("taxId", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder="Enter tax ID"
                                />
                                {errors.taxId && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.taxId}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Legal Status</label>
                                <select
                                    value={formData.legalStatus}
                                    onChange={e => handleInputChange("legalStatus", e.target.value)}
                                    className="w-full p-2 border rounded-lg">
                                    <option value="">Select Legal Status</option>
                                    {legalStatusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.legalStatus && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.legalStatus}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card> */}
                {/* Contact Information */}
                <Card color={undefined} className="p-6">
                    <h2 className="text-xl font-semibold mb-6">{t("companyProfileSettings.contactInformation")}</h2>
                    <div className="space-y-6">
                        {/* Business Address */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">{t("companyProfileSettings.businessAddress")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.streetAddress")}</label>
                                    <input
                                        type="text"
                                        value={formData.address.street}
                                        onChange={e =>
                                            handleInputChange("address", {
                                                ...formData.address,
                                                street: e.target.value,
                                            })
                                        }
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.streetAddressPlaceholder")}
                                    />
                                    {errors.street && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.street}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.city")}</label>
                                    <input
                                        type="text"
                                        value={formData.address.city}
                                        onChange={e =>
                                            handleInputChange("address", {
                                                ...formData.address,
                                                city: e.target.value,
                                            })
                                        }
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.cityPlaceholder")}
                                    />
                                    {errors.city && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.city}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.stateProvince")}</label>
                                    <input
                                        type="text"
                                        value={formData.address.state}
                                        onChange={e =>
                                            handleInputChange("address", {
                                                ...formData.address,
                                                state: e.target.value,
                                            })
                                        }
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.stateProvincePlaceholder")}
                                    />
                                    {errors.state && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.state}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.postalCode")}</label>
                                    <input
                                        type="text"
                                        value={formData.address.postalCode}
                                        onChange={e =>
                                            handleInputChange("address", {
                                                ...formData.address,
                                                postalCode: e.target.value,
                                            })
                                        }
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.postalCodePlaceholder")}
                                    />
                                    {errors.postalCode && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.postalCode}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.country")}</label>
                                    <CountryDropdown
                                        selectedCountry={selectedCountry}
                                        onCountrySelect={handleCountrySelect}
                                        placeholder={t("companyProfileSettings.selectCountryPlaceholder")}
                                        className="w-full"
                                    />
                                    {errors.country && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.country}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contact Details */}
                        <div>
                            <h3 className="text-lg font-medium mb-4">{t("companyProfileSettings.contactDetails")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.businessPhone")}</label>
                                    <PhoneInput
                                        value={formData.phone}
                                        onChange={value => handleInputChange("phone", value)}
                                        placeholder={t("companyProfileSettings.businessPhonePlaceholder")}
                                        className="w-full p-2 border rounded-lg"
                                    />
                                    {errors.phone && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.phone}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.businessEmail")}</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => handleInputChange("email", e.target.value)}
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.businessEmailPlaceholder")}
                                    />
                                    {errors.email && (
                                        <div className="flex items-center text-red-600 text-sm mt-1">
                                            <CircleAlert className="w-4 h-4 mr-1" />
                                            {errors.email}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t("companyProfileSettings.website")}</label>
                                    <input
                                        type="url"
                                        value={formData.website}
                                        onChange={e => handleInputChange("website", e.target.value)}
                                        className="w-full p-2 border rounded-lg"
                                        placeholder={t("companyProfileSettings.websitePlaceholder")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
                {/* Save Changes Button */}
                <div className="flex justify-end mt-4">
                    {company == null ? (
                        <button
                            className={`px-4 py-2 text-white rounded-md flex items-center ${
                                isLoading 
                                    ? "bg-blue-400 cursor-not-allowed" 
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                            onClick={handleSave}
                            disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    <span className="pl-2">{t("companyProfileSettings.saving")}</span>
                                </>
                            ) : (
                                <>
                                    <SquareCheckBig className="h-5" />
                                    <span className="pl-2">{t("companyProfileSettings.saveChanges")}</span>
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            className={`px-4 py-2 text-white rounded-md flex items-center ${
                                isLoading 
                                    ? "bg-blue-400 cursor-not-allowed" 
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                            onClick={handleSave}
                            disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    <span className="pl-2">{t("companyProfileSettings.updating")}</span>
                                </>
                            ) : (
                                <>
                                    <SquareCheckBig className="h-5" />
                                    <span className="pl-2">{t("companyProfileSettings.updateChanges")}</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
